import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { NotebookPen, ArrowLeft, FileCode2, Folder, FolderOpen, Sparkles, Loader2, RefreshCw, FileText, Cpu, Package, BookOpen, GitGraph, Braces, Wand2, Trash2, Download, GitBranch } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import ReadAlongside from "../components/ReadAlongside";
import MermaidDiagram from "../components/MermaidDiagram";

// Build a nested tree from a flat list of {path,...} rows.
function buildTree(files) {
  const root = { name: "", path: "", children: {}, files: [], isDir: true };
  files.forEach((f) => {
    const parts = f.path.split("/");
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      if (!node.children[key]) {
        node.children[key] = { name: key, path: parts.slice(0, i + 1).join("/"), children: {}, files: [], isDir: true };
      }
      node = node.children[key];
    }
    node.files.push(f);
  });
  return root;
}

// Flatten the nested tree into a list of visible rows so we can render
// without any recursive components (a Babel plugin used by the editor
// chokes on self-referencing JSX).
function flattenVisible(node, expanded, depth, out) {
  const dirs = Object.values(node.children).sort((a, b) => a.name.localeCompare(b.name));
  const files = [...node.files].sort((a, b) => a.name.localeCompare(b.name));
  for (const d of dirs) {
    const isOpen = expanded[d.path] ?? depth < 1;
    out.push({ kind: "dir", path: d.path, name: d.name, depth, isOpen });
    if (isOpen) flattenVisible(d, expanded, depth + 1, out);
  }
  for (const f of files) {
    out.push({ kind: "file", id: f.id, name: f.name, path: f.path, depth, file: f });
  }
}

export default function KnuthWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState(null);
  const [contentBusy, setContentBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [reanalyzing, setReanalyzing] = useState(false);
  const [docs, setDocs] = useState([]);
  const [activeDoc, setActiveDoc] = useState(null); // when set, center shows doc instead of file
  const [generating, setGenerating] = useState(null); // kind currently being generated
  const [viewMode, setViewMode] = useState("file"); // 'file' | 'doc'
  const [syncStatus, setSyncStatus] = useState(null); // { project_status, docs: [{id, sync}] }
  const [refreshingSource, setRefreshingSource] = useState(false);

  useEffect(() => {
    api.get(`/knuth/projects/${id}`)
      .then(({ data }) => {
        setProject(data);
        const readme = (data.files || []).find((f) => /^readme(\.|$)/i.test(f.name));
        const first = readme || (data.files || [])[0];
        if (first) setSelected(first);
      })
      .catch(() => {
        toast.error("Project not found");
        nav("/knuth");
      });
    api.get(`/knuth/projects/${id}/docs`).then(({ data }) => setDocs(data.docs || [])).catch(() => {});
  }, [id, nav]);

  // Sync status only makes sense for git-connected projects
  useEffect(() => {
    if (!project?.id) return;
    api.get(`/knuth/projects/${project.id}/sync-status`)
      .then(({ data }) => setSyncStatus(data))
      .catch(() => setSyncStatus(null));
  }, [project?.id, docs.length]);

  const filePathSet = useMemo(() => new Set((project?.files || []).map((f) => f.path)), [project]);
  const filesByPath = useMemo(() => {
    const map = {};
    (project?.files || []).forEach((f) => { map[f.path] = f; });
    return map;
  }, [project]);

  const docSyncMap = useMemo(() => {
    const m = {};
    (syncStatus?.docs || []).forEach((d) => { m[d.id] = d.sync; });
    return m;
  }, [syncStatus]);

  useEffect(() => {
    if (!selected) return;
    setContent(null);
    setContentBusy(true);
    api.get(`/knuth/projects/${id}/files/${selected.id}`)
      .then(({ data }) => setContent(data))
      .catch(() => toast.error("Could not load file"))
      .finally(() => setContentBusy(false));
  }, [id, selected]);

  const tree = useMemo(() => buildTree(project?.files || []), [project]);
  const visibleRows = useMemo(() => {
    const rows = [];
    if (tree) flattenVisible(tree, expanded, 0, rows);
    return rows;
  }, [tree, expanded]);

  const toggleDir = (p) => setExpanded((prev) => ({ ...prev, [p]: !(prev[p] ?? false) }));

  const reanalyze = async () => {
    setReanalyzing(true);
    try {
      const { data } = await api.post(`/knuth/projects/${id}/analyze`);
      const fresh = await api.get(`/knuth/projects/${id}`);
      setProject(fresh.data);
      toast.success(`Re-indexed · ${data.understanding?.file_count || 0} files`);
    } catch {
      toast.error("Re-analysis failed");
    } finally {
      setReanalyzing(false);
    }
  };

  const generateDoc = async (kind) => {
    const needsFile = kind === "explain" || kind === "pseudocode";
    if (needsFile && !selected) {
      toast.info("Pick a file first, then run this action.");
      return;
    }
    setGenerating(kind);
    try {
      const { data } = await api.post(`/knuth/projects/${id}/docs`, {
        kind,
        file_id: needsFile ? selected.id : undefined,
      });
      setDocs((prev) => [data, ...prev]);
      setActiveDoc(data);
      setViewMode("doc");
      toast.success(`${data.title} ready`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(null);
    }
  };

  const generateDiagram = async (kind) => {
    setGenerating(`diagram:${kind}`);
    try {
      const { data } = await api.post(`/knuth/projects/${id}/diagrams`, { kind });
      setDocs((prev) => [data, ...prev]);
      setActiveDoc(data);
      setViewMode("doc");
      toast.success(`${data.title} ready`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(null);
    }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Delete this generated document?")) return;
    try {
      await api.delete(`/knuth/docs/${docId}`);
      setDocs((prev) => prev.filter((d) => d.id !== docId));
      if (activeDoc?.id === docId) {
        setActiveDoc(null);
        setViewMode("file");
      }
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const openFile = (file) => {
    setSelected(file);
    setViewMode("file");
    setActiveDoc(null);
  };

  const openDoc = (doc) => {
    setActiveDoc(doc);
    setViewMode("doc");
  };

  const jumpToPath = (path) => {
    const target = filesByPath[path];
    if (target) openFile(target);
    else toast.error(`No file matching ${path}`);
  };

  const refreshSource = async () => {
    setRefreshingSource(true);
    try {
      await api.post(`/knuth/projects/${id}/refresh-source`);
      const fresh = await api.get(`/knuth/projects/${id}`);
      setProject(fresh.data);
      const sync = await api.get(`/knuth/projects/${id}/sync-status`);
      setSyncStatus(sync.data);
      toast.success("Pulled latest commit — old docs marked outdated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshingSource(false);
    }
  };

  const explainSelection = async (selection, startLine, endLine) => {
    if (!selected) return;
    setGenerating("explain-selection");
    try {
      const { data } = await api.post(`/knuth/projects/${id}/explain-selection`, {
        file_id: selected.id,
        selection,
        start_line: startLine,
        end_line: endLine,
      });
      setDocs((prev) => [data, ...prev]);
      setActiveDoc(data);
      setViewMode("doc");
      toast.success("Explanation ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not explain");
    } finally {
      setGenerating(null);
    }
  };

  if (!project) {
    return <div className="pt-32 text-center text-ink2">Loading workbench…</div>;
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col bg-cream" data-testid="knuth-workspace">
      {/* Top bar */}
      <div className="border-b border-border bg-cream/80 backdrop-blur px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/knuth" className="btn-ghost text-sm py-1.5" data-testid="knuth-back-btn">
            <ArrowLeft className="w-4 h-4" /> Projects
          </Link>
          <span className="text-ink2/40">·</span>
          <div className="min-w-0">
            <div className="font-heading text-lg font-semibold truncate">{project.name}</div>
            <div className="text-xs text-ink2 truncate">
              {project.understanding?.file_count || 0} files · {project.understanding?.primary_language || "no language detected"}
              {project.understanding?.top_modules?.length > 0 && (
                <> · {project.understanding.top_modules.slice(0, 3).join(" / ")}</>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {syncStatus?.project_status === "outdated" && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-marigold bg-marigold/10 rounded-full px-2 py-1" data-testid="sync-outdated-badge">
              Repo has new commits
            </span>
          )}
          {project.source === "git" && (
            <button onClick={refreshSource} disabled={refreshingSource} className="btn-ghost text-sm py-1.5 disabled:opacity-60" data-testid="knuth-refresh-source-btn">
              {refreshingSource ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitBranch className="w-4 h-4" />}
              Pull latest
            </button>
          )}
          <button onClick={reanalyze} disabled={reanalyzing} className="btn-ghost text-sm py-1.5 disabled:opacity-60" data-testid="knuth-reanalyze-btn">
            {reanalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Re-analyze
          </button>
        </div>
      </div>

      {/* Three-panel workspace */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-[280px_1fr_320px] min-h-0">
        {/* LEFT — Project explorer */}
        <aside className="border-r border-border overflow-y-auto bg-cream/60" data-testid="knuth-panel-left">
          <div className="px-3 py-3 border-b border-border/60">
            <div className="label-eyebrow inline-flex items-center gap-2"><Package className="w-3 h-3" /> Project</div>
          </div>
          {project.understanding?.file_count > 0 && (
            <div className="px-3 pt-3 pb-2 border-b border-border/60 space-y-2 text-[11px] text-ink2">
              {project.understanding?.languages && (
                <div>
                  <div className="uppercase tracking-widest font-semibold mb-1">Languages</div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(project.understanding.languages).slice(0, 8).map(([lang, n]) => (
                      <span key={lang} className="px-1.5 py-0.5 rounded bg-paper text-ink border border-border font-mono">{lang}·{n}</span>
                    ))}
                  </div>
                </div>
              )}
              {project.understanding?.manifests?.length > 0 && (
                <div>
                  <div className="uppercase tracking-widest font-semibold mb-1">Manifests</div>
                  <div className="text-ink font-mono text-[10px] space-y-0.5">
                    {project.understanding.manifests.slice(0, 4).map((m, i) => <div key={i} className="truncate">{m}</div>)}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="p-2">
            {project.files?.length === 0 ? (
              <div className="p-4 text-xs text-ink2">No files ingested.</div>
            ) : (
              visibleRows.map((row) => {
                if (row.kind === "dir") {
                  return (
                    <button
                      key={`d-${row.path}`}
                      onClick={() => toggleDir(row.path)}
                      className="w-full flex items-center gap-1.5 py-1 px-1.5 rounded hover:bg-paper text-left text-sm text-ink"
                      style={{ paddingLeft: 8 + row.depth * 12 }}
                      data-testid={`tree-dir-${row.path}`}
                    >
                      {row.isOpen
                        ? <FolderOpen className="w-3.5 h-3.5 text-marigold flex-shrink-0" />
                        : <Folder className="w-3.5 h-3.5 text-ink2 flex-shrink-0" />}
                      <span className="truncate">{row.name}</span>
                    </button>
                  );
                }
                const active = viewMode === "file" && selected?.id === row.id;
                return (
                  <button
                    key={`f-${row.id}`}
                    onClick={() => openFile(row.file)}
                    className={`w-full flex items-center gap-1.5 py-1 px-1.5 rounded text-left text-sm transition ${active ? "bg-teal/15 text-teal font-medium" : "text-ink2 hover:bg-paper hover:text-ink"}`}
                    style={{ paddingLeft: 8 + row.depth * 12 }}
                    data-testid={`tree-file-${row.id}`}
                  >
                    <FileCode2 className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{row.name}</span>
                  </button>
                );
              })
            )}
          </div>
          {docs.length > 0 && (
            <div className="border-t border-border/60 mt-2 p-2" data-testid="knuth-generated-docs">
              <div className="label-eyebrow inline-flex items-center gap-2 px-1.5 mb-2"><Sparkles className="w-3 h-3" /> Generated</div>
              {docs.map((d) => {
                const active = viewMode === "doc" && activeDoc?.id === d.id;
                const isDiagram = d.content_kind === "mermaid";
                const sync = docSyncMap[d.id];
                return (
                  <div key={d.id} className={`group flex items-center gap-1 rounded ${active ? "bg-teal/15" : "hover:bg-paper"}`}>
                    <button
                      onClick={() => openDoc(d)}
                      className={`flex-1 flex items-center gap-1.5 py-1 px-1.5 text-left text-sm ${active ? "text-teal font-medium" : "text-ink2"}`}
                      data-testid={`doc-open-${d.id}`}
                    >
                      {isDiagram ? <GitGraph className="w-3.5 h-3.5 flex-shrink-0" /> : <BookOpen className="w-3.5 h-3.5 flex-shrink-0" />}
                      <span className="truncate">{d.title}{d.file_path ? ` · ${d.file_path.split("/").pop()}` : ""}</span>
                      {sync === "outdated" && (
                        <span className="ml-auto text-[9px] uppercase tracking-widest font-bold text-marigold flex-shrink-0" data-testid={`doc-sync-${d.id}`}>stale</span>
                      )}
                      {sync === "current" && (
                        <span className="ml-auto text-[9px] uppercase tracking-widest font-bold text-teal flex-shrink-0" data-testid={`doc-sync-${d.id}`}>✓</span>
                      )}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteDoc(d.id); }}
                      className="opacity-0 group-hover:opacity-100 p-1 text-ink2 hover:text-red-500 transition"
                      data-testid={`doc-delete-${d.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </aside>

        {/* CENTER — Artifact viewer */}
        <main className="overflow-y-auto bg-cream" data-testid="knuth-panel-center">
          {viewMode === "doc" && activeDoc ? (
            <DocViewer
              doc={activeDoc}
              onSwitchToFile={() => setViewMode("file")}
              filePathSet={filePathSet}
              onJumpToPath={jumpToPath}
              syncStatus={docSyncMap[activeDoc.id]}
            />
          ) : !selected ? (
            <EmptyCenter />
          ) : contentBusy ? (
            <div className="p-10 text-center text-ink2 text-sm inline-flex items-center gap-2 justify-center w-full">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading {selected.name}…
            </div>
          ) : content ? (
            <FileViewer file={content} onExplainSelection={explainSelection} explaining={generating === "explain-selection"} />
          ) : (
            <div className="p-10 text-center text-ink2">Could not load file.</div>
          )}
        </main>

        {/* RIGHT — Knuth AI actions (Phase 2 live) */}
        <aside className="border-l border-border overflow-hidden flex flex-col bg-cream/60" data-testid="knuth-panel-right">
          <div className="px-4 py-3 border-b border-border/60">
            <div className="label-eyebrow inline-flex items-center gap-2"><Cpu className="w-3 h-3" /> Knuth AI</div>
            <p className="text-xs text-ink2 mt-1">Claude Sonnet 4.5 · knows this project.</p>
          </div>
          <div className="p-4 space-y-4 overflow-y-auto flex-1">
            <ReadAlongside
              seeds={`documentation architecture pseudocode readme ${project.understanding?.primary_language || ""} ${project.name || ""} ${(project.understanding?.top_modules || []).slice(0,3).join(" ")}`}
              eyebrow="Read alongside"
              title="chapters from the curriculum for docs & architecture"
              askPrompt={`I'm documenting the "${project.name}" project in Knuth. What curriculum ideas will help me write clearer docs?`}
              variant="ribbon"
              defaultOpen={false}
              testId="knuth-read-alongside"
              className="mb-1"
            />
            <GenerateGroup title="Documentation" icon={BookOpen}>
              <GenerateBtn onClick={() => generateDoc("readme")} busy={generating === "readme"} icon={FileText} label="Generate README" testid="gen-readme" />
              <GenerateBtn onClick={() => generateDoc("architecture")} busy={generating === "architecture"} icon={Package} label="Architecture doc" testid="gen-architecture" />
              <GenerateBtn onClick={() => generateDoc("api")} busy={generating === "api"} icon={Braces} label="API reference" testid="gen-api" />
            </GenerateGroup>

            <GenerateGroup title="This file" icon={FileCode2}>
              <GenerateBtn
                onClick={() => generateDoc("explain")}
                busy={generating === "explain"}
                icon={Wand2}
                label={selected ? `Explain ${selected.name}` : "Explain selected file"}
                testid="gen-explain"
                disabled={!selected}
              />
              <GenerateBtn
                onClick={() => generateDoc("pseudocode")}
                busy={generating === "pseudocode"}
                icon={Braces}
                label={selected ? `Pseudocode for ${selected.name}` : "Pseudocode for selected file"}
                testid="gen-pseudocode"
                disabled={!selected}
              />
            </GenerateGroup>

            <GenerateGroup title="Diagrams" icon={GitGraph}>
              <GenerateBtn onClick={() => generateDiagram("component")} busy={generating === "diagram:component"} icon={GitGraph} label="Component diagram" testid="gen-diagram-component" />
              <GenerateBtn onClick={() => generateDiagram("class")} busy={generating === "diagram:class"} icon={GitGraph} label="Class diagram" testid="gen-diagram-class" />
              <GenerateBtn onClick={() => generateDiagram("sequence")} busy={generating === "diagram:sequence"} icon={GitGraph} label="Sequence diagram" testid="gen-diagram-sequence" />
              <GenerateBtn onClick={() => generateDiagram("flowchart")} busy={generating === "diagram:flowchart"} icon={GitGraph} label="Program flowchart" testid="gen-diagram-flowchart" />
            </GenerateGroup>
          </div>
          <div className="px-4 py-3 border-t border-border/60 text-[10px] text-ink2 leading-relaxed">
            Every generated artifact appears under <span className="font-semibold">Generated</span> in the left panel. All AI actions consume model tokens — heavy repos may take 10-30s.
          </div>
        </aside>
      </div>
    </div>
  );
}

function EmptyCenter() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center p-10">
      <div className="w-16 h-16 rounded-2xl bg-teal/15 text-teal flex items-center justify-center mb-4">
        <NotebookPen className="w-8 h-8" />
      </div>
      <h2 className="font-heading text-2xl font-semibold mb-1">Pick a file to explore.</h2>
      <p className="text-sm text-ink2">Choose anything from the project explorer on the left.</p>
    </div>
  );
}

function FileViewer({ file, onExplainSelection, explaining }) {
  const [selection, setSelection] = useState(null); // { text, startLine, endLine }
  const [menuPos, setMenuPos] = useState(null); // { x, y }

  const onMouseUp = () => {
    const sel = window.getSelection();
    const text = sel?.toString() || "";
    if (!text.trim()) { setSelection(null); setMenuPos(null); return; }
    // Estimate lines by counting lines above the selection in the pre text
    const pre = document.querySelector('[data-testid="file-viewer-content"]');
    if (!pre || !sel?.rangeCount) { setSelection({ text }); setMenuPos(null); return; }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    // Rough line-number derivation: count \n before selection in the full text
    const fullText = pre.textContent || "";
    const before = fullText.slice(0, fullText.indexOf(text));
    const startLine = before ? before.split("\n").length : 1;
    const endLine = startLine + (text.split("\n").length - 1);
    setSelection({ text, startLine, endLine });
    setMenuPos({ x: rect.left + rect.width / 2, y: rect.bottom + window.scrollY });
  };

  const closeMenu = () => setMenuPos(null);

  return (
    <div className="max-w-full relative">
      <div className="sticky top-0 border-b border-border bg-cream/95 backdrop-blur px-6 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="file-viewer-header">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <FileText className="w-4 h-4 text-teal" />
            <span className="font-mono text-sm text-ink truncate">{file.path}</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-ink2 border border-border rounded-full px-2 py-0.5">{file.language}</span>
          </div>
          <div className="text-[11px] text-ink2 mt-0.5">{file.lines} lines · {(file.size / 1024).toFixed(1)} KB · <span className="italic">select any code to explain it</span></div>
        </div>
      </div>
      <pre
        onMouseUp={onMouseUp}
        onScroll={closeMenu}
        className="px-6 py-5 text-[13px] leading-relaxed font-mono text-ink whitespace-pre-wrap break-words select-text"
        data-testid="file-viewer-content"
      >
        {file.content}
      </pre>
      {menuPos && selection && (
        <div
          style={{ left: menuPos.x, top: menuPos.y + 8, transform: "translateX(-50%)" }}
          className="fixed z-40 bg-ink text-cream rounded-full shadow-[0_10px_30px_-8px_rgba(26,27,65,0.35)] flex items-center gap-2 px-2 py-1"
          data-testid="explain-selection-popover"
        >
          <button
            onClick={() => { onExplainSelection?.(selection.text, selection.startLine, selection.endLine); closeMenu(); }}
            disabled={explaining}
            className="text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-cream/10 inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="explain-selection-btn"
          >
            {explaining ? <Loader2 className="w-3.5 h-3.5 animate-spin text-saffron" /> : <Wand2 className="w-3.5 h-3.5 text-saffron" />}
            Explain this
          </button>
          <span className="text-[10px] font-mono text-cream/50 pr-2">L{selection.startLine}{selection.endLine !== selection.startLine ? `-${selection.endLine}` : ""}</span>
        </div>
      )}
    </div>
  );
}

function GenerateGroup({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-widest font-semibold text-ink2">
        <Icon className="w-3 h-3" /> {title}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function GenerateBtn({ onClick, busy, icon: Icon, label, testid, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={busy || disabled}
      className="w-full text-left text-sm px-3 py-2 rounded-lg border border-border hover:border-teal hover:bg-teal/5 transition text-ink2 hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-border disabled:hover:text-ink2 inline-flex items-center gap-2"
      data-testid={testid}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin text-marigold flex-shrink-0" /> : <Icon className="w-3.5 h-3.5 text-marigold flex-shrink-0" />}
      <span className="truncate">{busy ? "Generating…" : label}</span>
    </button>
  );
}

function DocViewer({ doc, onSwitchToFile, filePathSet, onJumpToPath, syncStatus }) {
  const isDiagram = doc.content_kind === "mermaid";
  const download = () => {
    const ext = isDiagram ? ".mmd" : ".md";
    const blob = new Blob([doc.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${doc.title.replace(/\s+/g, "-").toLowerCase()}${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Extract file-path references present in the doc for the traceability sidebar
  const refs = useMemo(() => {
    if (isDiagram || !doc.content || !filePathSet) return [];
    const found = [];
    const seen = new Set();
    const paths = [...filePathSet].sort((a, b) => b.length - a.length); // match longer paths first
    for (const p of paths) {
      if (doc.content.includes(p) && !seen.has(p)) {
        seen.add(p);
        found.push(p);
      }
    }
    return found;
  }, [doc, filePathSet, isDiagram]);

  // Custom react-markdown renderers: turn code text that equals a known path into a jump link.
  const linkifyCode = ({ node, inline, className, children, ...props }) => {
    const text = String(children).replace(/\n$/, "");
    if (inline && filePathSet?.has(text)) {
      return (
        <button
          type="button"
          onClick={() => onJumpToPath?.(text)}
          className="font-mono text-[13px] px-1.5 py-0.5 rounded bg-teal/10 text-teal hover:bg-teal/20 border border-teal/30 transition inline-flex items-center gap-1"
          data-testid={`doc-trace-${text}`}
        >
          {text}
        </button>
      );
    }
    return <code className={className} {...props}>{children}</code>;
  };

  return (
    <div className="max-w-full" data-testid="doc-viewer">
      <div className="sticky top-0 border-b border-border bg-cream/95 backdrop-blur px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isDiagram ? <GitGraph className="w-4 h-4 text-teal" /> : <BookOpen className="w-4 h-4 text-teal" />}
            <span className="font-heading text-lg font-semibold truncate">{doc.title}</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-ink2 border border-border rounded-full px-2 py-0.5">{doc.kind}</span>
            {syncStatus === "outdated" && (
              <span className="text-[10px] uppercase tracking-widest font-bold text-marigold bg-marigold/10 rounded-full px-2 py-0.5" data-testid="doc-viewer-sync-outdated">
                Outdated · pull latest to refresh
              </span>
            )}
            {syncStatus === "current" && (
              <span className="text-[10px] uppercase tracking-widest font-bold text-teal bg-teal/10 rounded-full px-2 py-0.5" data-testid="doc-viewer-sync-current">
                Current
              </span>
            )}
          </div>
          {doc.file_path && (
            <div className="text-[11px] text-ink2 mt-0.5 font-mono">
              from{" "}
              <button onClick={() => onJumpToPath?.(doc.file_path) || onSwitchToFile()} className="underline hover:text-teal" data-testid="doc-viewsource-btn">
                {doc.file_path}
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={download} className="btn-ghost text-sm py-1.5" data-testid="doc-download-btn">
            <Download className="w-3.5 h-3.5" /> Download
          </button>
        </div>
      </div>
      <div className="px-6 py-6" data-testid="doc-viewer-content">
        {isDiagram ? (
          <MermaidDiagram source={doc.content} testid="doc-mermaid" />
        ) : (
          <div className="grid lg:grid-cols-[1fr_220px] gap-6">
            <article className="markdown-doc prose prose-sm max-w-none">
              <ReactMarkdown components={{ code: linkifyCode }}>{doc.content}</ReactMarkdown>
            </article>
            {refs.length > 0 && (
              <aside className="text-xs" data-testid="doc-traceability">
                <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><FileCode2 className="w-3 h-3" /> Referenced sources</div>
                <ul className="space-y-1">
                  {refs.map((p) => (
                    <li key={p}>
                      <button
                        onClick={() => onJumpToPath?.(p)}
                        className="font-mono text-[11px] text-teal hover:text-marigold text-left break-all"
                        data-testid={`doc-trace-ref-${p}`}
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                </ul>
              </aside>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
